import { HTTP_OPTIONS, AD_ENTITY_TYPES } from '@silverfort-nodelibs/common/utils/consts';
import { DualSelectList, SnackAlert, SnackAlertContent } from '@silverfort/sf-components';
import { FC, useState, useMemo, useCallback, RefObject } from 'react';
import { httpGet } from '../../../../../../components/utils';
import { useServiceAccountContext } from '../../../contexts/ServiceAccountContext';
import { ADOwnerData, OwnersAddedBy } from '../../../ServiceAccountsTypes';

const OwnerSelect: FC<{
    anchorEl: RefObject<HTMLElement>;
    menuOpen: boolean;
    onClose: () => void;
    maxOwners: number;
}> = ({ anchorEl, menuOpen, onClose, maxOwners }) => {
    const { updateServiceAccountData, selectedOwnersData } = useServiceAccountContext();

    const onOwnerSelect = useCallback(
        (ownersGuids: string[]) => {
            const ownersGuidsAddedBy: { [guid: string]: OwnersAddedBy } = {};
            selectedOwnersData?.forEach((owner) => {
                ownersGuidsAddedBy[owner.guid] = owner.added_by;
            });
            const accountsOwners: ADOwnerData[] = ownersGuids.map((guid) => ({
                owner_guid: guid,
                added_by: ownersGuidsAddedBy[guid] || OwnersAddedBy.UI
            }));
            updateServiceAccountData('account_owners', accountsOwners);
        },
        [updateServiceAccountData, selectedOwnersData]
    );
    const [openMaxOwnerAlert, setOpenMaxOwnerAlert] = useState(false);

    const getEntityTypeFromTab = useCallback((tab: string) => {
        if (tab === 'all') {
            return [AD_ENTITY_TYPES.USER, AD_ENTITY_TYPES.GROUP];
        }
        if (tab === 'accounts') {
            return [AD_ENTITY_TYPES.USER];
        }
        if (tab === 'groups') {
            return [AD_ENTITY_TYPES.GROUP];
        }
    }, []);

    const getTheOwnerOptions = useCallback(
        async (searchQuery: string, tabValue?: string) => {
            if (!searchQuery) {
                return [];
            }
            const ownersData: {
                data: {
                    Results: { name: string; guid: string; domain_name: string; path: string; is_disabled }[];
                } | null;
            } = await httpGet(`/users/searchEntitiesByTypes/${searchQuery}`, {
                [HTTP_OPTIONS.REQ_OPTIONS]: {
                    params: {
                        hideDeleted: true,
                        entitiesTypes: getEntityTypeFromTab(tabValue || 'all'),
                        authTypes: ['ad']
                    }
                }
            });

            return (
                ownersData.data?.Results.map(({ name, guid, domain_name, path, is_disabled }) => ({
                    title: name + `${is_disabled ? ' (disabled)' : ''}`,
                    titles: {
                        display: name + `${is_disabled ? ' (disabled)' : ''}`,
                        full: path + `${is_disabled ? ' (disabled)' : ''}`
                    },
                    value: guid,
                    subtitle: `(${domain_name})`
                })) || []
            );
        },
        [getEntityTypeFromTab]
    );

    const onSelect = useCallback(
        (val: string | string[] | undefined) => {
            if (Array.isArray(val) && val.length > maxOwners) {
                setOpenMaxOwnerAlert(true);
                return;
            }
            onOwnerSelect(val ? (Array.isArray(val) ? val : [val]) : []);
            onClose();
        },
        [onOwnerSelect, onClose, maxOwners]
    );

    const selectedItems = useMemo(() => {
        return selectedOwnersData?.map((o) => {
            return {
                title: o.name + `${o.is_disabled ? ' (disabled)' : ''}`,
                titles: {
                    display: o.name + `${o.is_disabled ? ' (disabled)' : ''}`,
                    full: o.path + `${o.is_disabled ? ' (disabled)' : ''}`
                },
                value: o.guid,
                subtitle: `(${o.domain})`
            };
        });
    }, [selectedOwnersData]);

    return (
        <div className="flex gap-4">
            <SnackAlert
                anchorOrigin={{
                    horizontal: 'center',
                    vertical: 'bottom'
                }}
                open={openMaxOwnerAlert}>
                <SnackAlertContent
                    message="Service accounts can be assigned up to five owners"
                    onClose={() => {
                        setOpenMaxOwnerAlert(false);
                    }}
                />
            </SnackAlert>

            <DualSelectList
                multiple
                menuPopupClassName="z-[7000] -top-60 -left-11"
                defaultExamplesTitle="Search for specific users, security groups or organizational units"
                title="Owners"
                menuProps={{
                    anchorEl: anchorEl.current,
                    open: menuOpen
                }}
                getItems={getTheOwnerOptions}
                searchable
                selectedItems={selectedItems}
                onChange={onSelect}
                onClose={onClose}
                searchPlaceholder="Search by name"
                width={232}
                tabs={[
                    {
                        name: 'All',
                        value: 'all'
                    },
                    {
                        name: 'Accounts',
                        value: 'accounts'
                    },
                    {
                        name: 'Groups',
                        value: 'groups'
                    }
                ]}
                titleViewDropdownItems={[
                    {
                        title: 'Display name',
                        value: 'display'
                    },
                    {
                        title: 'Full path',
                        value: 'full'
                    }
                ]}
            />
        </div>
    );
};

export default OwnerSelect;
